<template>
  <div class="bg-white p-4 rounded-md">
    <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">{{ $t("eligibility.title") }}</p>
    <p class="text-xs italic text-gray-600 pb-2">{{ $t("eligibility.subtitle") }}</p>

    <div class="mt-2 space-y-3">
      <!-- Question: how did the customer obtain the device? -->
      <div>
        <RadioGroup v-model="selectedOption" class="mb-0">
          <RadioGroupLabel class="block text-sm font-semibold text-gray-900">
            {{ $t("eligibility.question") }}
          </RadioGroupLabel>
          <div class="flex gap-2 mt-2 flex-col">
            <RadioGroupOption
              v-for="option in options"
              :key="option.value"
              as="template"
              :value="option.value"
              v-slot="{ checked }"
            >
              <div
                :class="[
                  'flex items-center border rounded-lg px-3 py-3 cursor-pointer transition-colors',
                  checked ? 'border-primary-dark bg-white' : 'border-gray-300 bg-white hover:border-gray-400',
                ]"
              >
                <span
                  :class="[
                    'flex h-4 w-4 items-center justify-center rounded-full border-2 mr-3',
                    checked ? 'border-primary-dark' : 'border-gray-300',
                  ]"
                >
                  <span v-if="checked" class="h-2 w-2 rounded-full bg-primary-dark" />
                </span>
                <span class="flex-1 text-sm text-gray-900">
                  <strong>{{ $t(`eligibility.options.${option.value}.bold`) }}</strong>
                  {{ $t(`eligibility.options.${option.value}.normal`) }}
                </span>
                <span v-if="checked" class="text-primary-dark text-sm ml-2">&#10003;</span>
              </div>
            </RadioGroupOption>
          </div>
        </RadioGroup>
      </div>

      <!-- Eligible message (option 1) -->
      <div v-if="selectedOption === 'distributor_under_30'" class="p-3 rounded-md bg-green-50 border border-green-200">
        <div class="flex items-start gap-2">
          <span class="text-green-600 text-sm">&#9989;</span>
          <div>
            <p class="text-sm font-semibold text-green-800">{{ $t("eligibility.eligible.title") }}</p>
            <p class="text-xs text-green-700 mt-0.5">{{ $t("eligibility.eligible.message") }}</p>
          </div>
        </div>
      </div>

      <!-- Additional conditions (option 2 or 3) -->
      <div v-if="showAdditionalConditions" class="p-3 rounded-md bg-yellow-50 border border-yellow-200">
        <div class="flex items-start gap-2">
          <span class="text-yellow-600 text-sm">&#9888;&#65039;</span>
          <div>
            <p class="text-sm font-semibold text-yellow-800">{{ $t("eligibility.conditions.title") }}</p>
            <ul class="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-0.5">
              <li>{{ $t("eligibility.conditions.item1") }}</li>
              <li>{{ $t("eligibility.conditions.item2") }}</li>
              <li>{{ $t("eligibility.conditions.item3") }}</li>
              <li>{{ $t("eligibility.conditions.item4") }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Checkbox confirmation (outside the yellow block) -->
      <label
        v-if="showAdditionalConditions"
        :class="[
          'flex items-center gap-2 p-3 rounded-md border-2 cursor-pointer transition-colors',
          conditionsConfirmed ? 'border-primary bg-primary-light' : 'border-gray-300 bg-white',
        ]"
      >
        <input
          v-model="conditionsConfirmed"
          type="checkbox"
          class="rounded-sm border-gray-300 text-primary-dark focus:ring-primary"
        />
        <span class="text-xs text-gray-900">{{ $t("eligibility.conditions.checkbox") }}</span>
      </label>

      <!-- Eligibility conditions to confirm -->
      <div v-if="showNotEligible" class="p-3 rounded-md bg-orange-50 border border-orange-300">
        <div class="flex items-start gap-2">
          <span class="text-orange-600 text-sm">&#9888;&#65039;</span>
          <div>
            <p class="text-sm font-semibold text-orange-800">{{ $t("eligibility.notEligible.title") }}</p>
            <p class="text-xs text-orange-700 mt-0.5">{{ $t("eligibility.notEligible.message") }}</p>
          </div>
        </div>
      </div>

      <!-- Action button -->
      <div v-if="selectedOption" class="mt-4 flex justify-end">
        <SecondaryButton
          :disabled="!isEligible"
          :class="{ 'cursor-not-allowed opacity-60': !isEligible }"
          @click="onContinue"
        >
          {{ $t("eligibility.continue") }}
        </SecondaryButton>
      </div>
    </div>
  </div>
</template>

<script>
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { SecondaryButton } from "@estaly/ui";

const OPTIONS = [{ value: "distributor_under_30" }, { value: "distributor_over_30" }, { value: "other_professional" }];

export default {
  name: "EligibilityForm",
  components: {
    RadioGroup,
    RadioGroupLabel,
    RadioGroupOption,
    SecondaryButton,
  },
  emits: ["continue"],
  data() {
    return {
      options: OPTIONS,
      selectedOption: null,
      conditionsConfirmed: false,
    };
  },
  computed: {
    showAdditionalConditions() {
      return this.selectedOption === "distributor_over_30" || this.selectedOption === "other_professional";
    },
    showNotEligible() {
      return this.showAdditionalConditions && !this.conditionsConfirmed;
    },
    isEligible() {
      if (this.selectedOption === "distributor_under_30") return true;
      if (this.showAdditionalConditions && this.conditionsConfirmed) return true;
      return false;
    },
  },
  watch: {
    selectedOption() {
      this.conditionsConfirmed = false;
    },
  },
  methods: {
    onContinue() {
      if (this.isEligible) {
        this.$emit("continue", this.selectedOption);
      }
    },
  },
};
</script>
